import type { GameDefinition, GameContext } from '../../platform/types';
import { clamp, difficultyFor, moveToward, overlaps, type Bullet, type Enemy } from './engine';

const WORLD_WIDTH = 390;
const WORLD_HEIGHT = 720;

function mount(container: HTMLElement, context: GameContext): () => void {
  container.innerHTML = `
    <section class="game-shell" aria-label="스타 가디언 게임">
      <header class="game-hud">
        <button class="icon-button exit-button" type="button" aria-label="메인 화면으로 돌아가기">←</button>
        <div><span class="hud-label">SCORE</span><strong data-score>0000</strong></div>
        <div class="hud-status"><span class="status-dot"></span><span>ONLINE</span></div>
      </header>
      <div class="canvas-frame">
        <canvas class="game-canvas" aria-label="손가락으로 우주선을 움직여 적을 피하고 격추하세요"></canvas>
        <div class="game-overlay intro-overlay">
          <p class="eyebrow">MISSION 01</p>
          <h2>STAR GUARDIAN</h2>
          <p>화면을 누른 채 움직이세요.<br />레이저는 자동으로 발사됩니다.</p>
          <button class="primary-button start-button" type="button">게임 시작</button>
        </div>
        <div class="game-overlay game-over-overlay" hidden>
          <p class="eyebrow">SIGNAL LOST</p>
          <h2>GAME OVER</h2>
          <p>최종 점수 <strong data-final-score>0</strong></p>
          <button class="primary-button restart-button" type="button">다시 시작</button>
          <button class="text-button home-button" type="button">게임 목록으로</button>
        </div>
      </div>
      <p class="touch-hint">화면 어디든 드래그해 이동</p>
    </section>`;

  function requiredElement<T extends Element>(selector: string): T {
    const element = container.querySelector<T>(selector);
    if (!element) throw new Error(`Star Guardian is missing ${selector}.`);
    return element;
  }

  const canvas = requiredElement<HTMLCanvasElement>('.game-canvas');
  const scoreElement = requiredElement<HTMLElement>('[data-score]');
  const finalScoreElement = requiredElement<HTMLElement>('[data-final-score]');
  const introOverlay = requiredElement<HTMLElement>('.intro-overlay');
  const gameOverOverlay = requiredElement<HTMLElement>('.game-over-overlay');
  const startButton = requiredElement<HTMLButtonElement>('.start-button');
  const restartButton = requiredElement<HTMLButtonElement>('.restart-button');
  const exitButtons = container.querySelectorAll<HTMLButtonElement>('.exit-button, .home-button');
  const drawingContext = canvas.getContext('2d');
  if (!drawingContext) throw new Error('Canvas 2D is not available.');
  const drawing: CanvasRenderingContext2D = drawingContext;

  let animationFrame = 0;
  let running = false;
  let lastTime = 0;
  let shotTimer = 0;
  let enemyTimer = 0;
  let elapsed = 0;
  let score = 0;
  let pointerActive = false;
  const player = { x: WORLD_WIDTH / 2 - 20, y: WORLD_HEIGHT - 108, width: 40, height: 54 };
  const target = { x: player.x, y: player.y };
  let bullets: Bullet[] = [];
  let enemies: Enemy[] = [];
  const stars = Array.from({ length: 54 }, (_, index) => ({
    x: (index * 83.17) % WORLD_WIDTH,
    y: (index * 47.53) % WORLD_HEIGHT,
    size: 0.7 + (index % 4) * 0.45,
    speed: 18 + (index % 6) * 8,
  }));

  function resizeCanvas(): void {
    const frame = canvas.parentElement;
    if (!frame) return;
    const bounds = frame.getBoundingClientRect();
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.max(1, Math.floor(bounds.width * ratio));
    canvas.height = Math.max(1, Math.floor(bounds.height * ratio));
    canvas.style.width = `${bounds.width}px`;
    canvas.style.height = `${bounds.height}px`;
    drawing.setTransform(canvas.width / WORLD_WIDTH, 0, 0, canvas.height / WORLD_HEIGHT, 0, 0);
  }

  function pointerPosition(event: PointerEvent): void {
    const bounds = canvas.getBoundingClientRect();
    target.x = clamp(((event.clientX - bounds.left) / bounds.width) * WORLD_WIDTH - player.width / 2, 8, WORLD_WIDTH - player.width - 8);
    target.y = clamp(((event.clientY - bounds.top) / bounds.height) * WORLD_HEIGHT - player.height / 2, 70, WORLD_HEIGHT - player.height - 16);
  }

  function onPointerDown(event: PointerEvent): void {
    if (!running) return;
    pointerActive = true;
    canvas.setPointerCapture(event.pointerId);
    pointerPosition(event);
  }

  function onPointerMove(event: PointerEvent): void {
    if (!running || !pointerActive) return;
    pointerPosition(event);
  }

  function onPointerUp(event: PointerEvent): void {
    pointerActive = false;
    if (canvas.hasPointerCapture(event.pointerId)) canvas.releasePointerCapture(event.pointerId);
  }

  function reset(): void {
    score = 0;
    shotTimer = 0;
    enemyTimer = 0;
    elapsed = 0;
    bullets = [];
    enemies = [];
    player.x = WORLD_WIDTH / 2 - player.width / 2;
    player.y = WORLD_HEIGHT - 108;
    target.x = player.x;
    target.y = player.y;
    scoreElement.textContent = '0000';
  }

  function start(): void {
    reset();
    introOverlay.hidden = true;
    gameOverOverlay.hidden = true;
    running = true;
    lastTime = performance.now();
    cancelAnimationFrame(animationFrame);
    animationFrame = requestAnimationFrame(loop);
  }

  function endGame(): void {
    running = false;
    pointerActive = false;
    finalScoreElement.textContent = String(score);
    gameOverOverlay.hidden = false;
    draw();
  }

  function spawnEnemy(): void {
    const width = 34 + Math.random() * 18;
    const settings = difficultyFor(score);
    enemies.push({
      x: 12 + Math.random() * (WORLD_WIDTH - width - 24),
      y: -54,
      width,
      height: width * 0.78,
      speed: settings.enemySpeed * (0.82 + Math.random() * 0.38),
      drift: 14 + Math.random() * 28,
      phase: Math.random() * Math.PI * 2,
    });
  }

  function update(delta: number): void {
    elapsed += delta;
    shotTimer += delta;
    enemyTimer += delta;

    player.x = moveToward(player.x, target.x, 520 * delta);
    player.y = moveToward(player.y, target.y, 520 * delta);

    if (shotTimer >= 0.19) {
      shotTimer %= 0.19;
      bullets.push({ x: player.x + player.width / 2 - 2, y: player.y - 12, width: 4, height: 18, speed: 590 });
    }

    const settings = difficultyFor(score);
    if (enemyTimer >= settings.enemyInterval) {
      enemyTimer %= settings.enemyInterval;
      spawnEnemy();
    }

    bullets.forEach((bullet) => { bullet.y -= bullet.speed * delta; });
    enemies.forEach((enemy) => {
      enemy.y += enemy.speed * delta;
      enemy.x += Math.sin(elapsed * 2.2 + enemy.phase) * enemy.drift * delta;
      enemy.x = clamp(enemy.x, 4, WORLD_WIDTH - enemy.width - 4);
    });

    bullets = bullets.filter((bullet) => bullet.y + bullet.height > 0);
    enemies = enemies.filter((enemy) => enemy.y < WORLD_HEIGHT + 70);

    const hitBullets = new Set<Bullet>();
    const hitEnemies = new Set<Enemy>();
    for (const enemy of enemies) {
      if (overlaps(player, enemy)) {
        endGame();
        return;
      }
      for (const bullet of bullets) {
        if (!hitBullets.has(bullet) && overlaps(bullet, enemy)) {
          hitBullets.add(bullet);
          hitEnemies.add(enemy);
          score += 10;
          scoreElement.textContent = String(score).padStart(4, '0');
          break;
        }
      }
    }
    bullets = bullets.filter((bullet) => !hitBullets.has(bullet));
    enemies = enemies.filter((enemy) => !hitEnemies.has(enemy));
  }

  function drawShip(): void {
    const center = player.x + player.width / 2;
    drawing.save();
    drawing.shadowColor = '#5cf2d6';
    drawing.shadowBlur = 18;
    drawing.fillStyle = '#d8fff8';
    drawing.beginPath();
    drawing.moveTo(center, player.y);
    drawing.lineTo(player.x + player.width, player.y + player.height);
    drawing.lineTo(center, player.y + player.height - 12);
    drawing.lineTo(player.x, player.y + player.height);
    drawing.closePath();
    drawing.fill();
    drawing.fillStyle = '#5cf2d6';
    drawing.fillRect(center - 5, player.y + 19, 10, 19);
    drawing.fillStyle = '#ff5caa';
    drawing.beginPath();
    drawing.moveTo(center - 7, player.y + player.height - 7);
    drawing.lineTo(center, player.y + player.height + 13 + Math.random() * 6);
    drawing.lineTo(center + 7, player.y + player.height - 7);
    drawing.fill();
    drawing.restore();
  }

  function drawEnemy(enemy: Enemy): void {
    drawing.save();
    drawing.translate(enemy.x + enemy.width / 2, enemy.y + enemy.height / 2);
    drawing.shadowColor = '#ff5caa';
    drawing.shadowBlur = 14;
    drawing.fillStyle = '#ff5caa';
    drawing.beginPath();
    drawing.moveTo(0, enemy.height / 2);
    drawing.lineTo(-enemy.width / 2, -enemy.height / 3);
    drawing.lineTo(-enemy.width / 5, -enemy.height / 2);
    drawing.lineTo(0, -enemy.height / 5);
    drawing.lineTo(enemy.width / 5, -enemy.height / 2);
    drawing.lineTo(enemy.width / 2, -enemy.height / 3);
    drawing.closePath();
    drawing.fill();
    drawing.fillStyle = '#2d1026';
    drawing.fillRect(-enemy.width * 0.2, -2, enemy.width * 0.4, 6);
    drawing.restore();
  }

  function draw(): void {
    const gradient = drawing.createLinearGradient(0, 0, 0, WORLD_HEIGHT);
    gradient.addColorStop(0, '#060916');
    gradient.addColorStop(0.58, '#10102b');
    gradient.addColorStop(1, '#170c25');
    drawing.fillStyle = gradient;
    drawing.fillRect(0, 0, WORLD_WIDTH, WORLD_HEIGHT);

    for (const star of stars) {
      const y = (star.y + elapsed * star.speed) % WORLD_HEIGHT;
      drawing.globalAlpha = 0.35 + star.size * 0.22;
      drawing.fillStyle = '#c5fff6';
      drawing.fillRect(star.x, y, star.size, star.size * 2.2);
    }
    drawing.globalAlpha = 1;

    drawing.strokeStyle = 'rgba(92, 242, 214, 0.08)';
    drawing.lineWidth = 1;
    for (let x = 0; x <= WORLD_WIDTH; x += 39) {
      drawing.beginPath(); drawing.moveTo(x, 0); drawing.lineTo(x, WORLD_HEIGHT); drawing.stroke();
    }

    drawing.shadowColor = '#5cf2d6';
    drawing.shadowBlur = 12;
    drawing.fillStyle = '#5cf2d6';
    bullets.forEach((bullet) => drawing.fillRect(bullet.x, bullet.y, bullet.width, bullet.height));
    drawing.shadowBlur = 0;
    enemies.forEach(drawEnemy);
    drawShip();
  }

  function loop(now: number): void {
    if (!running) return;
    const delta = Math.min((now - lastTime) / 1000, 0.033);
    lastTime = now;
    update(delta);
    draw();
    if (running) animationFrame = requestAnimationFrame(loop);
  }

  const observer = new ResizeObserver(resizeCanvas);
  observer.observe(canvas.parentElement ?? canvas);
  canvas.addEventListener('pointerdown', onPointerDown);
  canvas.addEventListener('pointermove', onPointerMove);
  canvas.addEventListener('pointerup', onPointerUp);
  canvas.addEventListener('pointercancel', onPointerUp);
  startButton.addEventListener('click', start);
  restartButton.addEventListener('click', start);
  exitButtons.forEach((button) => button.addEventListener('click', context.onExit));
  resizeCanvas();
  draw();

  return () => {
    running = false;
    cancelAnimationFrame(animationFrame);
    observer.disconnect();
    canvas.removeEventListener('pointerdown', onPointerDown);
    canvas.removeEventListener('pointermove', onPointerMove);
    canvas.removeEventListener('pointerup', onPointerUp);
    canvas.removeEventListener('pointercancel', onPointerUp);
  };
}

const game: GameDefinition = {
  id: 'star-guardian',
  title: '스타 가디언',
  tagline: 'DEEP SPACE · SCORE ATTACK',
  description: '손끝으로 기체를 조종하고 몰려오는 적 함대를 격추하세요.',
  accent: '#5cf2d6',
  icon: '✦',
  controls: '드래그 이동 · 자동 발사',
  mount,
};

export default game;
